import React, { useEffect, useRef, useState } from "react";
import "./RefreshRatePicker.css";

/**
 * RefreshRatePicker — Grafana-style "Refresh | 5s ▾" widget.
 *
 * Two visual parts, same row:
 *   1. a Refresh button that calls onManualRefresh() every click
 *   2. a dropdown whose selection is a polling interval in ms (or null = Off)
 *
 * The caller owns the actual polling logic via the `value` prop. We just
 * surface a choice and fire onChange; the parent wires setInterval.
 *
 * Options are the intersection of "useful for an operational dashboard"
 * and "Grafana's standard list" — we skip >5m because anything slower
 * than that isn't really "live" anymore.
 *
 * Props:
 *   value              number | null — current interval (ms) or null=off
 *   onChange(ms)       called with the new interval (null for Off)
 *   onManualRefresh()  called when the user clicks the Refresh button
 *   refreshing         optional boolean — shows a spinner on the button
 *   disabled           optional boolean — disables everything
 *   className          optional extra class
 */

export const DEFAULT_REFRESH_OPTIONS = [
  { label: "Off", value: null },
  { label: "5s", value: 5000 },
  { label: "10s", value: 10000 },
  { label: "30s", value: 30000 },
  { label: "1m", value: 60000 },
  { label: "5m", value: 300000 },
];

export default function RefreshRatePicker({
  value,
  onChange,
  onManualRefresh,
  refreshing = false,
  disabled = false,
  options = DEFAULT_REFRESH_OPTIONS,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const current = options.find((o) => o.value === value) || options[0];

  const pick = (opt) => {
    onChange(opt.value);
    setOpen(false);
  };

  return (
    <div className={`rrp ${className}`} ref={rootRef}>
      <button
        type="button"
        className={`rrp-refresh-btn ${refreshing ? "spinning" : ""}`}
        onClick={onManualRefresh}
        disabled={disabled}
        title="Refresh now"
        aria-label="Refresh now"
      >
        <span className="rrp-refresh-icon">↻</span>
        <span className="rrp-refresh-label">Refresh</span>
      </button>
      <button
        type="button"
        className="rrp-rate-trigger"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Auto-refresh interval"
      >
        <span className="rrp-rate-label">{current.label}</span>
        <span className="rrp-caret">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="rrp-dropdown" role="listbox">
          {options.map((opt) => (
            <div
              key={opt.label}
              role="option"
              aria-selected={opt.value === value}
              className={`rrp-option ${opt.value === value ? "selected" : ""}`}
              onClick={() => pick(opt)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
